import type { PlotType } from 'plotly.js'

import { AppInstanceInfo, CpuArchitecture } from './application'
import { Process } from './security'
import { AppVersionStatus } from './version'

export type InstanceData = {
  id: string
  activeAgo: number
  companyId: number
  companyName: string
  currentApplicationId: string
  currentApplicationFile: string
  currentApplicationName: string
  currentApplicationVersion: string
  currentApplicationVersionStatus: AppVersionStatus
  isCommonInstance: boolean
  isGuest: boolean
  isPool: boolean
  openAgo: number
  readonly: boolean
  status: string
  /** Minutes until the instance is recycled, null when it never expires. */
  timeoutIn: number | null
  userName: string
  userFullName: string
  fromTask: boolean
  scheduledTaskId: string | null
  scheduledTaskName: string | null
  engineURI: string
  engineUID: string
  engineParams: string
  hasOpenedBrowser: boolean
  cpuArchitecture: CpuArchitecture | ''
  isAgent: boolean
  poolUserName: string
  /** Whether the user's email is in the AI full-access domains; null for agents and pools. */
  isInternalUser: boolean | null
}

export interface InstanceCardProps {
  instance: InstanceData
  isCurrentInstance: boolean
  closingInstance: { id: string; showLoader: boolean }
  cardClickHandler: () => Promise<void>
  killInstanceHandler: (id: string) => void
  isMobile?: boolean
}

export enum LanguageValues {
  Es = 'es-AR',
  En = 'en-US',
  Pt = 'pt-BR'
}
export enum QuickMenuValues {
  Default = 'default',
  FromInterface = 'from_interface'
}
export enum NumberLocaleValues {
  Es = 'es',
  En = 'en'
}
export type UserInstanceData = {
  id: string
  userId: string
  userFullName: string
  userFirstName: string
  userLastName: string
  userName: string
  userCompanyId: number
  userIsSuperUser: boolean
  userIsStaff: boolean
  userHome: string
  companyId: number
  company_code: string
  companyName: string
  process: Process[]
  applicationInfo: AppInstanceInfo
  imageUrl: string
  loginModule: string
  autoopenUri: string
  numberLocale: NumberLocaleValues
  userNumberLocale: NumberLocaleValues | null
  language: LanguageValues
  uiExperience?: string
  uiExperienceEnabled?: boolean
  supportEnabled?: boolean
  companySettingFile: string
  loginAction: loginAction
  created_at: string
  departments: string[]
  groups: string[]
  sessionTimeout: number
  sessionTimeoutForProcessing: number
  guestSessionTimeout: number
  version: string
}

interface loginAction {
  open_app?: string
}

export interface InstanceTableProps {
  instances: InstanceData[]
  handleSelect: (i: InstanceData) => void
  selectedInstance: string | null
  handleRequestKillInstanceClick: (sk: string) => void
  handleKillInstanceClick: (instance: InstanceData) => void
  sort: string
  setSort: React.Dispatch<React.SetStateAction<string>>
  loading: boolean
}

export interface InstancesResult {
  userInstance: UserInstanceData
  activeInstances: InstanceData[]
  hasActiveInstances: boolean
}

export interface PodResources {
  podName: string
  totalMemory: number
  usedCPU: number
  usedMemory: number
  lastUpdate: string
}

export interface SystemResources {
  totalMemory: number
  usedCPU: number
  usedMemory: number
  disk: Disk
  apiData?: Data[]
  instanceData?: Data[]
  instance?: Instance
  podName?: string
}
interface Disk {
  size: string
  used: string
  percent: string
}
export interface Data {
  fill?: 'none' | 'tozeroy' | 'tozerox' | 'tonexty' | 'tonextx' | 'toself' | 'tonext'
  marker: Marker
  name: string
  type: PlotType
  x: string[]
  y: number[]
  yaxis?: string
}
interface Instance {
  currentNode: string
  maxMemory: number
  pid: number
  totalMemory: number
  usedCPU: number
  usedMemory: number
}
interface Marker {
  color: string
}
interface PerformanceMonitor {
  usedMemory: string
  data?: Partial<Plotly.PlotData>[]
}
export interface ApiPerformanceMonitorProps extends PerformanceMonitor {
  totalMemory: string
  usedCPU: string
  diskSize: string
  selectedInstance: string | null
  diskPercent: string
  pods?: PodResources[]
  onResetPods?: () => void
}
export interface InstancePerformanceMonitorProps extends PerformanceMonitor {
  memoryQuota: string
  pid?: number
  modelMemoryPeak: string
  lastProcessedNode: string
  checked: boolean
  currentApplicationPath?: string
  usedCPU: string
}

interface ResourceDefinitionItem {
  cpu: string
  ram: string
}

interface ResourceDefinition {
  requests: ResourceDefinitionItem
  limits: ResourceDefinitionItem
}

export interface LegacyResourceItem {
  nodeSelector: string
  resources: ResourceDefinition
}

export interface EngineResourceItem {
  metadata?: { cpu_architecture?: string }
  pod_definition?: { spec?: { nodeSelector?: Record<string, string> } }
  container_params?: {
    resources?: {
      requests?: { cpu?: string; memory?: string }
      limits?: { cpu?: string; memory?: string }
    }
  }
}

export type ResourceItem = LegacyResourceItem | EngineResourceItem

export interface ResourceOption {
  label: string
  value: ResourceItem
}
